import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { finalize, firstValueFrom } from 'rxjs';
import { ProcessingStateService } from '../../services/processing-state.service';
import {
    type TranslationFile,
    type TranslationApiItem,
    TranslationsService,
} from '../../services/translations.service';

type TranslationStatusClass =
    | 'is-complete'
    | 'is-failed'
    | 'is-queued';

type TranslationDisplayStatus = {
    label: string;
    className: TranslationStatusClass;
};

@Component({
    selector: 'app-translations',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './translations.component.html',
    styleUrl: './translations.component.css',
})
export class TranslationsComponent implements OnInit {
    readonly translations = signal<TranslationApiItem[]>([]);
    readonly isLoading = signal(false);
    readonly errorMessage = signal<string | null>(null);
    readonly downloadErrorMessage = signal<string | null>(null);
    readonly deleteErrorMessage = signal<string | null>(null);
    readonly downloadingSubmissionIds = signal<ReadonlySet<string>>(new Set<string>());
    readonly deletingSubmissionIds = signal<ReadonlySet<string>>(new Set<string>());
    readonly translationPendingDelete = signal<TranslationApiItem | null>(null);

    constructor(
        private readonly processingState: ProcessingStateService,
        private readonly translationsService: TranslationsService
    ) { }

    ngOnInit(): void {
        this.loadTranslations();
    }

    loadTranslations(): void {
        this.processingState.setProcessing(true);
        this.isLoading.set(true);
        this.errorMessage.set(null);
        this.downloadErrorMessage.set(null);
        this.deleteErrorMessage.set(null);

        this.translationsService.getTranslations().pipe(
            finalize(() => {
                this.isLoading.set(false);
                this.processingState.setProcessing(false);
            })
        ).subscribe({
            next: (translations) => {
                this.translations.set(translations);
            },
            error: () => {
                this.translations.set([]);
                this.errorMessage.set('Unable to load translations.');
            },
        });
    }

    trackTranslation(index: number, translation: TranslationApiItem): string | number {
        return translation.id ?? `${this.getFriendlyName(translation)}-${index}`;
    }

    getFriendlyName(translation: TranslationApiItem): string {
        return this.firstText(translation.friendlyName) ?? 'Untitled translation';
    }

    getOriginalFileName(translation: TranslationApiItem): string {
        return (
            this.firstText(
                translation.originalFile.fileName
            ) ?? 'Not available'
        );
    }

    getExpectedOutputFileName(translation: TranslationApiItem): string {
        return this.firstText(translation.expectedOutputFile.fileName) ?? 'Not available';
    }

    getTranslatedFileName(translation: TranslationApiItem): string {
        return translation.isCompleted
            ? this.firstText(translation.translatedFile?.fileName) ?? 'Not generated'
            : 'Not generated';
    }

    getSampleDataFileNames(translation: TranslationApiItem): string[] {
        const sampleDataFileNames = this.normalizeSampleDataFiles(translation.sampleDataFiles);
        if (sampleDataFileNames.length > 0) return sampleDataFileNames;
        return ['Not available'];
    }

    getStatus(translation: TranslationApiItem): TranslationDisplayStatus {
        if (translation.translationFailed) {
            return { label: 'Failed', className: 'is-failed' };
        }

        if (translation.isCompleted) {
            return { label: 'Complete', className: 'is-complete' };
        }

        return {
            label: this.firstText(translation.statusMessage) ?? 'Queued',
            className: 'is-queued',
        };
    }

    canDownloadTranslation(translation: TranslationApiItem): boolean {
        return (
            translation.isCompleted &&
            this.firstText(translation.submissionId) !== null &&
            this.firstText(translation.translatedFile?.fileName) !== null
        );
    }

    isDownloadingTranslation(translation: TranslationApiItem): boolean {
        const submissionId = this.firstText(translation.submissionId);
        return submissionId !== null && this.downloadingSubmissionIds().has(submissionId);
    }

    canDeleteTranslation(translation: TranslationApiItem): boolean {
        return this.firstText(translation.submissionId) !== null;
    }

    isDeletingTranslation(translation: TranslationApiItem): boolean {
        const submissionId = this.firstText(translation.submissionId);
        return submissionId !== null && this.deletingSubmissionIds().has(submissionId);
    }

    getDownloadButtonLabel(translation: TranslationApiItem): string {
        return `Download ${this.getTranslatedFileName(translation)}`;
    }

    getDeleteButtonLabel(translation: TranslationApiItem): string {
        return `Delete ${this.getFriendlyName(translation)}`;
    }

    getDeleteConfirmationName(): string {
        const translation = this.translationPendingDelete();
        return translation ? this.getFriendlyName(translation) : 'this translation';
    }

    async downloadTranslation(translation: TranslationApiItem): Promise<void> {
        const submissionId = this.firstText(translation.submissionId);
        const fileName = this.firstText(translation.translatedFile?.fileName);

        if (!translation.isCompleted || !submissionId || !fileName || this.isDownloadingTranslation(translation)) {
            return;
        }

        this.setDownloading(submissionId, true);
        this.processingState.setProcessing(true);
        this.downloadErrorMessage.set(null);

        try {
            const file = await firstValueFrom(
                this.translationsService.downloadTranslatedFile(submissionId, fileName)
            );

            this.saveFile(file, fileName);
        } catch {
            this.downloadErrorMessage.set(`Unable to download ${fileName}.`);
        } finally {
            this.setDownloading(submissionId, false);
            this.processingState.setProcessing(false);
        }
    }

    openDeleteDialog(translation: TranslationApiItem): void {
        if (!this.canDeleteTranslation(translation) || this.isDeletingTranslation(translation)) {
            return;
        }

        this.deleteErrorMessage.set(null);
        this.translationPendingDelete.set(translation);
    }

    closeDeleteDialog(): void {
        const translation = this.translationPendingDelete();

        if (translation && this.isDeletingTranslation(translation)) {
            return;
        }

        this.translationPendingDelete.set(null);
    }

    async confirmDeleteTranslation(): Promise<void> {
        const translation = this.translationPendingDelete();
        const submissionId = this.firstText(translation?.submissionId);

        if (!translation || !submissionId || this.isDeletingTranslation(translation)) {
            return;
        }

        const friendlyName = this.getFriendlyName(translation);

        this.setDeleting(submissionId, true);
        this.processingState.setProcessing(true);
        this.deleteErrorMessage.set(null);

        try {
            await firstValueFrom(this.translationsService.deleteTranslation(submissionId));
            this.translations.update((translations) =>
                translations.filter((item) => this.firstText(item.submissionId) !== submissionId)
            );
            this.translationPendingDelete.set(null);
        } catch {
            this.translationPendingDelete.set(null);
            this.deleteErrorMessage.set(`Delete was not successful for ${friendlyName}. Please try again.`);
        } finally {
            this.setDeleting(submissionId, false);
            this.processingState.setProcessing(false);
        }
    }

    private normalizeSampleDataFiles(files: TranslationFile[] | null | undefined): string[] {
        if (!files) return [];

        return files
            .map((file) =>
                typeof file === 'string' ? file : this.firstText(file.fileName)
            )
            .map((fileName) => this.firstText(fileName))
            .filter((fileName): fileName is string => fileName !== null);
    }

    private firstText(...values: Array<string | null | undefined>): string | null {
        for (const value of values) {
            if (this.hasText(value)) return value.trim();
        }

        return null;
    }

    private hasText(value: string | null | undefined): value is string {
        return typeof value === 'string' && value.trim().length > 0;
    }

    private setDownloading(submissionId: string, isDownloading: boolean): void {
        const downloadingSubmissionIds = new Set(this.downloadingSubmissionIds());

        if (isDownloading) {
            downloadingSubmissionIds.add(submissionId);
        } else {
            downloadingSubmissionIds.delete(submissionId);
        }

        this.downloadingSubmissionIds.set(downloadingSubmissionIds);
    }

    private setDeleting(submissionId: string, isDeleting: boolean): void {
        const deletingSubmissionIds = new Set(this.deletingSubmissionIds());

        if (isDeleting) {
            deletingSubmissionIds.add(submissionId);
        } else {
            deletingSubmissionIds.delete(submissionId);
        }

        this.deletingSubmissionIds.set(deletingSubmissionIds);
    }

    private saveFile(file: Blob, fileName: string): void {
        const objectUrl = URL.createObjectURL(file);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = fileName;
        document.body.append(link);
        link.click();
        link.remove();

        setTimeout(() => URL.revokeObjectURL(objectUrl));
    }
}
